import React, { Component, Children } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import ModalCloseContainer from './ModalCloseContainer';
import TakeoverDrawer from '../../SiteTakeovers/TakeoverDrawer';

// It appears the iPhone X bug is actually fixed by merely
// specifying the webkit-overflow-scrolling property
// on the child element.
//
// I left "scrollFix" as an option in case this bug is
// not reproduced in the simulator or we have problems
// with other modals, and in part as a historical record
// of this bug if we ever see it again and scratch our heads,
// but from what I can see `auto` fixes the issue.

const TakeoverBackdrop = styled.div`
  background: ${({ backdropColor }) =>
    backdropColor ? backdropColor : 'white'};
  display: flex;
  flex-direction: column;
  height: 100%;
  left: 0;
  ${props =>
    props.scrollFix
      ? '-webkit-overflow-scrolling: auto;'
      : '-webkit-overflow-scrolling: touch;'} overflow: auto;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 10;
`;

const OverscrollBackdrop = styled.div`
  height: 150%;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 0;
`;

// This modal container is intended for use on mobile
export default class TakeoverModal extends Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    scrollFix: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      isDrawerOpen: false,
      scrollY: typeof window !== 'undefined' ? window.scrollY : 0,
    };
  }

  componentDidMount() {
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${this.state.scrollY}px`;

    this.setState({
      isDrawerOpen: true,
    });
  }

  closeClicked(closeButtonName: string) {
    if (this.props.slideIn) {
      this.setState({
        isDrawerOpen: false,
        closeButtonName,
      });
    } else {
      this.triggerModalClose(closeButtonName);
    }
  }

  triggerModalClose(closeButtonName: string) {
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    window.scrollTo(0, this.state.scrollY);

    this.props.closeAction(closeButtonName || this.state.closeButtonName);
  }

  render() {
    const { className, slideIn, useAltCloseButton } = this.props;

    let children = this.props.children;

    if (useAltCloseButton) {
      children = Children.map(this.props.children, child => {
        return React.cloneElement(child, {
          closeAction: closeButtonName => this.closeClicked(closeButtonName),
        });
      });
    }

    const closeButton = !useAltCloseButton ? (
      <ModalCloseContainer
        isMobile={true}
        closeCallback={() => this.closeClicked('default-close')}
      />
    ) : null;

    if (slideIn) {
      return (
        <TakeoverBackdrop
          className={`pp-modal-takeover ${className}`}
          backdropColor="transparent"
          onClick={e => e.stopPropagation()}
        >
          <TakeoverDrawer
            direction="bottom"
            maxWidth="100%"
            maxHeight="100%"
            open={this.state.isDrawerOpen}
            finishedClosing={() => this.triggerModalClose()}
            overlayColor="transparent"
          >
            {closeButton}
            {children}
            <OverscrollBackdrop />
          </TakeoverDrawer>
        </TakeoverBackdrop>
      );
    }

    return (
      <TakeoverBackdrop
        className="pp-modal-takeover"
        onClick={e => e.stopPropagation()}
      >
        {closeButton}
        {children}
        <OverscrollBackdrop />
      </TakeoverBackdrop>
    );
  }
}
